import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngxs/store';
import { LimitedTermDuration } from '@platform-frontend/checkout-drop-in';
import {
  AssetState,
  DesktopDrawerService,
  FlowState,
  ProductState,
  ShoppingCartState,
  StartupConfigurationState
} from '@platform-frontend/checkout';
import {
  ASSET_OWNERSHIP_STATUS_TAG_CONFIGURATIONS,
  AssetOwnershipStatus,
  isLimitedTermDuration,
  LicenseKind,
  LicenseType,
  LicenseValidityContext,
  LicenseValidityDuration,
  ProductCategory,
  ProductLocalization
} from '@platform-frontend/marketplace';
import { MobileService, StatusTagConfiguration } from '@platform-frontend/ui-tools';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';

@Component({
  selector: 'yat-commercial-header',
  templateUrl: './commercial-header.component.html',
  styleUrls: ['./commercial-header.component.scss']
})
export class CommercialHeaderComponent implements OnInit {
  constructor(
    private readonly mobileService: MobileService,
    private readonly store: Store,
    private readonly translocoService: TranslocoService,
    private readonly desktopDrawerService: DesktopDrawerService
  ) {}

  @Input()
  id: string;

  @ViewChild('desktopDrawerContent')
  desktopDrawerContent!: TemplateRef<any>;

  readonly assetOwnershipStatus$: Observable<AssetOwnershipStatus | null> = this.store.select(AssetState.assetOwnershipStatus);
  readonly endOfPeriod$: Observable<Date | null> = this.store.select(AssetState.endOfPeriod);
  readonly maxNumberOfPersons$: Observable<number | null> = this.store.select(ProductState.maxNumberOfPersons);
  readonly productLocalization$: Observable<ProductLocalization | null> = this.store.select(ProductState.productLocalization);
  readonly vendorName$: Observable<string | null> = this.store.select(ProductState.vendorName);
  readonly productCategory$: Observable<ProductCategory | null> = this.store.select(ProductState.productCategory);
  readonly groupName$: Observable<string | null> = this.store.select(StartupConfigurationState.groupName);
  readonly selectedQuantity$: Observable<number | null> = this.store.select(ShoppingCartState.selectedQuantity);
  readonly showStatusTag$: Observable<boolean> = combineLatest([this.productCategory$, this.store.select(AssetState.isOneTimePurchase)]).pipe(
    map(([category, isOneTimePurchase]) => (category === 'TRAVEL_TICKET' ? !isOneTimePurchase : true))
  );
  readonly OWNERSHIP_STATUS_TAG_CONFIGURATIONS: (canceledTooltipDate?: string, tooltipClass?: string) => StatusTagConfiguration<AssetOwnershipStatus>[] = (
    canceledTooltipDate?: string,
    tooltipClass?: string
  ) => ASSET_OWNERSHIP_STATUS_TAG_CONFIGURATIONS(this.translocoService, canceledTooltipDate, tooltipClass);

  licenseKind$: Observable<LicenseKind | null>;
  licenseValidity$: Observable<LicenseValidityContext | null>;
  productVariant$: Observable<string | null>;
  enableShoppingCartConfigurator$: Observable<boolean>;
  showDialogExtendedHeader: boolean = false;

  ngOnInit(): void {
    const isOrderToBeManaged$: Observable<boolean> = this.store.select(FlowState.isOrderToBeManaged);

    const licenseType$: Observable<LicenseType | null> = isOrderToBeManaged$.pipe(
      switchMap(managingAsset => (managingAsset ? this.store.select(AssetState.licenseType) : this.store.select(ShoppingCartState.selectedLicenseType)))
    );
    this.licenseKind$ = licenseType$.pipe(map(licenseType => licenseType?.kind ?? null));

    this.licenseValidity$ = isOrderToBeManaged$.pipe(
      switchMap(managingAsset => (managingAsset ? this.store.select(AssetState.licenseValidity) : this.store.select(ShoppingCartState.selectedValidity)))
    );

    this.productVariant$ = isOrderToBeManaged$.pipe(
      switchMap(isOrderToBeManaged => this.store.select(isOrderToBeManaged ? AssetState.productVariant : ProductState.productVariant))
    );

    this.enableShoppingCartConfigurator$ = isOrderToBeManaged$.pipe(
      switchMap(isOrderToBeManaged => (isOrderToBeManaged ? of(false) : this.store.select(ShoppingCartState.canShoppingCartBeConfigured)))
    );
  }

  isMobile(): boolean {
    return this.mobileService.isMobileScreenSize();
  }

  closeDialog(): void {
    if (this.isMobile()) {
      this.closeMobileDialog();
    } else {
      this.closeDesktopDrawer();
    }
  }

  closeMobileDialog(): void {
    this.showDialogExtendedHeader = false;
  }

  showMobileDialog(): void {
    if (this.isMobile()) {
      this.showDialogExtendedHeader = true;
    } else {
      this.openDesktopDrawer();
    }
  }

  openDesktopDrawer(): void {
    if (this.desktopDrawerContent) {
      this.desktopDrawerService.openDrawer(this.desktopDrawerContent);
    }
  }

  closeDesktopDrawer(): void {
    this.desktopDrawerService.closeDrawer();
  }

  getLimitedTermDuration(validityDuration: LicenseValidityDuration | null): LimitedTermDuration | null {
    return isLimitedTermDuration(validityDuration) ? validityDuration : null;
  }
}
